import * as React from "react";
import { useNavigate, useParams } from "react-router";
import { ShieldCheck } from "lucide-react";
import { format } from "date-fns";

import { AuthShell } from "@/components/auth/AuthShell";
import {
  AuthBanner,
  AuthField,
  AuthFoot,
  AuthHeading,
  AuthLink,
  AuthPrimaryButton,
  PasswordInput,
  PasswordRules,
  passwordMeetsPolicy,
} from "@/components/auth/AuthForm";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/states";
import {
  useInvitationByToken,
  type InvitationByToken,
} from "@/api/invitations";
import { useAcceptMyInvitation } from "@/api/myInvitations";
import { useAcceptInvite } from "@/api/auth";
import { apiErrorStatus, apiRequestId } from "@/api/error";
import { useAuthStore } from "@/store/authStore";

/**
 * /accept-invite/:token — canvas 01b `auth/InviteSetPassword`,
 * `InviteCrossSignedOut`, `InviteWrongAccount`, `InviteExpired` (MV2-909).
 *
 * Branches on `invitation_type` from GET /invitations/by-token:
 *
 *   home          brand-new user → set a password → POST /auth/accept-invite
 *                 → /login?accepted=1.
 *   cross_client  existing user (MV2-205). Signed out → sign in with
 *                 ?next back here. Signed in as someone else → a sign-out
 *                 button, not an instruction. Signed in right → review and
 *                 accept → POST /me/cross-client-invitations/{id}/accept.
 *
 * Expired, already-accepted and a failed lookup are three different
 * screens. The invitee and the expiry are named on every one of them.
 */

const HERO_HOME = {
  headline: "Welcome aboard.",
  sub: "Pick a password and you're in. Your team's sites, alarms and tickets are already waiting.",
};
const HERO_CROSS = {
  headline: "Access across companies, on your own login.",
  sub: "Accept another client's offer with the account you already have. Revoke it any time from Settings → Access.",
};

const fmtWhen = (iso: string) => format(new Date(iso), "EEE d MMM HH:mm");
const firstName = (full: string) => full.trim().split(/\s+/)[0] || full;

function isExpired(inv: InvitationByToken, now = Date.now()): boolean {
  if (inv.status === "expired") return true;
  return (
    inv.status === "pending" &&
    !!inv.expires_at &&
    new Date(inv.expires_at).getTime() < now
  );
}

export function AcceptInvitePage(): React.ReactElement {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const invitation = useInvitationByToken(token);
  const accept = useAcceptInvite();
  const acceptCrossClient = useAcceptMyInvitation();
  const authStatus = useAuthStore((s) => s.status);
  const authedEmail = useAuthStore((s) => s.email);
  const signOutAll = useAuthStore((s) => s.signOutAll);

  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [switching, setSwitching] = React.useState(false);

  const inv = invitation.data;
  const isCross = inv?.invitation_type === "cross_client";
  const hero = isCross ? HERO_CROSS : HERO_HOME;

  // ── lookup states ───────────────────────────────────────────────
  if (invitation.isLoading || !token) {
    return (
      <AuthShell {...HERO_HOME}>
        <div aria-busy="true" role="status" className="flex flex-col gap-3">
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-56" />
          <Skeleton className="mt-5 h-[62px] w-full rounded-[12px]" />
          <Skeleton className="h-[34px] w-full rounded-[8px]" />
          <Skeleton className="h-[34px] w-full rounded-[8px]" />
        </div>
      </AuthShell>
    );
  }

  if (invitation.isError || !inv) {
    const status = apiErrorStatus(invitation.error);
    if (status === 404) {
      return (
        <AuthShell {...HERO_HOME}>
          <AuthHeading
            title="This link isn't valid"
            sub="It may have been revoked or replaced by a newer invitation."
          />
          <p className="text-[13px] text-gray-700">
            Ask the person who invited you for a fresh link. Already have an
            account? <AuthLink to="/login">Sign in</AuthLink>.
          </p>
        </AuthShell>
      );
    }
    const requestId = apiRequestId(invitation.error);
    return (
      <AuthShell {...HERO_HOME}>
        <AuthHeading title="We couldn't check this invitation" />
        <AuthBanner tone="bad" align="start" className="mb-5">
          <div className="flex flex-col gap-0.5">
            <span className="text-[12.5px] font-medium">
              The invitation service didn&apos;t respond.
            </span>
            <span className="text-[11px] leading-[1.4] text-gray-500">
              Your link is still good
              {requestId ? (
                <>
                  {" "}
                  (request{" "}
                  <span className="font-mono tabular">{requestId}</span>)
                </>
              ) : null}
              . Try again in a moment.
            </span>
          </div>
        </AuthBanner>
        <AuthPrimaryButton
          type="button"
          onClick={() => void invitation.refetch()}
        >
          Try again
        </AuthPrimaryButton>
        <AuthFoot left={<AuthLink to="/login">← Back to sign in</AuthLink>} />
      </AuthShell>
    );
  }

  // ── expired / already accepted / withdrawn ──────────────────────
  if (isExpired(inv)) {
    return (
      <AuthShell headline={HERO_HOME.headline} sub="">
        <AuthHeading
          title="This link has expired"
          sub={
            inv.expires_at ? (
              <>
                This one ran out on{" "}
                <b className="font-semibold text-gray-900">
                  {fmtWhen(inv.expires_at)}
                </b>
                .
              </>
            ) : undefined
          }
        />
        <InviteCard
          who={inv.invited_user_name_snapshot}
          line={
            <>
              {inv.inviting_client_name} · sent to {inv.invited_email_snapshot}{" "}
              · link valid until <b className="font-semibold">expired</b>
            </>
          }
        />
        <p className="text-[13px] text-gray-700">
          Ask the person who invited you to resend it — one click on the Users
          page. Already have an account?{" "}
          <AuthLink to="/login">Sign in</AuthLink>.
        </p>
      </AuthShell>
    );
  }

  if (inv.status === "accepted") {
    return (
      <AuthShell {...hero}>
        <AuthHeading
          title="This invitation was already accepted"
          sub="Your account is set up — sign in to continue."
        />
        <InviteCard
          who={inv.invited_user_name_snapshot}
          line={
            <>
              {inv.inviting_client_name} · {inv.invited_email_snapshot}
            </>
          }
        />
        <AuthPrimaryButton
          type="button"
          onClick={() =>
            navigate(
              `/login?email=${encodeURIComponent(inv.invited_email_snapshot)}`,
            )
          }
        >
          Sign in
        </AuthPrimaryButton>
      </AuthShell>
    );
  }

  if (inv.status !== "pending") {
    return (
      <AuthShell {...hero}>
        <AuthHeading
          title="This invitation was withdrawn"
          sub="The person who sent it revoked it, or it was declined."
        />
        <InviteCard
          who={inv.invited_user_name_snapshot}
          line={
            <>
              {inv.inviting_client_name} · {inv.invited_email_snapshot}
            </>
          }
        />
        <p className="text-[13px] text-gray-700">
          Ask the person who invited you for a fresh one. Already have an
          account? <AuthLink to="/login">Sign in</AuthLink>.
        </p>
      </AuthShell>
    );
  }

  const validUntil = inv.expires_at ? (
    <>
      {" "}
      · link valid until{" "}
      <b className="font-semibold">{fmtWhen(inv.expires_at)}</b>
    </>
  ) : null;

  // ── cross-client ────────────────────────────────────────────────
  if (isCross) {
    const card = (
      <InviteCard
        who="Cross-client access"
        line={
          <>
            From {inv.inviting_client_name} · sent to{" "}
            {inv.invited_email_snapshot}
            {validUntil}
          </>
        }
      />
    );

    if (authStatus === "loading") {
      return (
        <AuthShell {...HERO_CROSS}>
          <div aria-busy="true" role="status" className="flex flex-col gap-3">
            <Skeleton className="h-7 w-64" />
            <Skeleton className="h-4 w-72" />
            <Skeleton className="mt-5 h-[62px] w-full rounded-[12px]" />
          </div>
        </AuthShell>
      );
    }

    if (authStatus !== "authenticated") {
      return (
        <AuthShell {...HERO_CROSS}>
          <AuthHeading
            title="Review your invitation"
            sub={`${inv.inviting_client_name} is offering you access to their sites.`}
          />
          {card}
          <AuthBanner tone="info">
            You already have a renewableBot account. Sign in to review and
            accept — no new password needed.
          </AuthBanner>
          <div className="mt-5">
            <AuthPrimaryButton
              type="button"
              onClick={() =>
                navigate(
                  `/login?next=${encodeURIComponent(`/accept-invite/${token}`)}&email=${encodeURIComponent(inv.invited_email_snapshot)}`,
                  { replace: true },
                )
              }
            >
              Sign in to continue
            </AuthPrimaryButton>
          </div>
        </AuthShell>
      );
    }

    const wrongAccount =
      !!authedEmail &&
      authedEmail.toLowerCase() !== inv.invited_email_snapshot.toLowerCase();

    if (wrongAccount) {
      const switchAccount = async () => {
        setSwitching(true);
        try {
          await signOutAll();
        } finally {
          navigate(
            `/login?next=${encodeURIComponent(`/accept-invite/${token}`)}&email=${encodeURIComponent(inv.invited_email_snapshot)}`,
            { replace: true },
          );
        }
      };
      return (
        <AuthShell headline={HERO_CROSS.headline} sub="">
          <AuthHeading title="Wrong account for this invitation" />
          {card}
          <AuthBanner tone="warn">
            This invitation was sent to{" "}
            <b className="font-semibold">{inv.invited_email_snapshot}</b> but
            you&apos;re signed in as{" "}
            <b className="font-semibold">{authedEmail}</b>.
          </AuthBanner>
          <div className="mt-5 flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              className="h-[42px] flex-1 text-[13px]"
              onClick={() => void switchAccount()}
              disabled={switching}
            >
              {switching ? "Signing out…" : "Sign out and switch"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="md"
              className="h-[42px] text-[13px]"
              onClick={() => navigate("/", { replace: true })}
              disabled={switching}
            >
              Cancel
            </Button>
          </div>
        </AuthShell>
      );
    }

    const acceptCross = async () => {
      if (!inv.invitation_id) return;
      try {
        await acceptCrossClient.mutateAsync(inv.invitation_id);
        // /me refetches in the mutation's onSuccess so the new grant is
        // live on landing.
        navigate("/", { replace: true });
      } catch {
        /* surfaced from acceptCrossClient.error below */
      }
    };
    return (
      <AuthShell {...HERO_CROSS}>
        <AuthHeading
          title="Review your invitation"
          sub={`${inv.inviting_client_name} is offering you access to their sites.`}
        />
        {card}
        <AuthBanner tone="info">
          Accepting gives <b className="font-semibold">{authedEmail}</b> access
          to the sites {inv.inviting_client_name} listed. Revoke it any time
          from Settings → Access.
        </AuthBanner>
        {acceptCrossClient.isError && (
          <AuthBanner tone="bad" className="mt-3.5">
            {(acceptCrossClient.error as Error).message ||
              "We couldn't accept the invitation. Try again, or ask the person who invited you."}
          </AuthBanner>
        )}
        <div className="mt-5">
          <AuthPrimaryButton
            type="button"
            onClick={() => void acceptCross()}
            loading={acceptCrossClient.isPending}
            loadingLabel="Accepting…"
          >
            Accept invitation
          </AuthPrimaryButton>
        </div>
        <AuthFoot
          left={
            <AuthLink onClick={() => navigate("/", { replace: true })}>
              Not now
            </AuthLink>
          }
        />
      </AuthShell>
    );
  }

  // ── home: set a password ────────────────────────────────────────
  const confirmMismatch = confirm.length > 0 && confirm !== password;
  const canSubmit =
    passwordMeetsPolicy(password) && password === confirm && !accept.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !token) return;
    try {
      const result = await accept.mutateAsync({
        token,
        new_password: password,
      });
      navigate(`/login?email=${encodeURIComponent(result.email)}&accepted=1`, {
        replace: true,
      });
    } catch {
      /* accept.error is rendered below */
    }
  }

  return (
    <AuthShell {...HERO_HOME}>
      <form onSubmit={handleSubmit} noValidate>
        <AuthHeading
          title={`Hi ${firstName(inv.invited_user_name_snapshot)} — you're invited`}
          sub="Set a password to activate your account."
        />
        <InviteCard
          who={inv.invited_user_name_snapshot}
          line={
            <>
              Joining {inv.inviting_client_name} · sent to{" "}
              {inv.invited_email_snapshot}
              {validUntil}
            </>
          }
        />
        {accept.isError && (
          <AuthBanner tone="bad" className="mb-3.5">
            {acceptSentence(accept.error)}
          </AuthBanner>
        )}
        <div className="flex flex-col gap-3.5">
          <AuthField label="Password" htmlFor="invite-password">
            <PasswordInput
              id="invite-password"
              autoFocus
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={accept.isPending}
            />
          </AuthField>
          <PasswordRules password={password} />
          <AuthField
            label="Confirm password"
            htmlFor="invite-confirm"
            err={confirmMismatch ? "Passwords don't match yet." : undefined}
          >
            <PasswordInput
              id="invite-confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              invalid={confirmMismatch}
              disabled={accept.isPending}
            />
          </AuthField>
        </div>
        <div className="mt-5">
          <AuthPrimaryButton
            type="submit"
            loading={accept.isPending}
            loadingLabel="Setting password…"
            disabled={!canSubmit}
          >
            Set password &amp; continue
          </AuthPrimaryButton>
        </div>
        <AuthFoot
          left={
            <span className="text-[12.5px] text-gray-500">
              Not you? Ask the person who invited you for a fresh link
            </span>
          }
        />
      </form>
    </AuthShell>
  );
}

/** Board `inviteCard`: shield + who, then the one meta line. */
function InviteCard({
  who,
  line,
}: {
  who: React.ReactNode;
  line: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="mb-[18px] rounded-[12px] border border-gray-200 bg-surface px-3.5 py-3">
      <div className="flex items-center gap-2">
        <ShieldCheck
          size={15}
          aria-hidden
          className="flex-none text-[color:var(--action)]"
        />
        <span className="text-[13px] font-medium text-gray-900">{who}</span>
      </div>
      <div className="mt-1 text-[11px] leading-[1.4] text-gray-500">{line}</div>
    </div>
  );
}

function acceptSentence(err: unknown): string {
  const message = err instanceof Error ? err.message : "";
  if (/expired/i.test(message)) {
    return "This link expired while you were here. Ask the person who invited you for a fresh one.";
  }
  if (/policy|InvalidPassword/i.test(message)) {
    return "Cognito rejected that password — it must meet every rule above.";
  }
  return "We couldn't set your password. Try again, or ask the person who invited you.";
}
